import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { LatLng } from '~/_shared/types/latLng';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import { type AgsReportCountry } from '~/exportReport/exportReport.constants';
import { ExportReportType } from '~/exportReport/exportReportType';
import type {
  ExportReportRadiusParams, ExportReportSampleParams, ExportReportSampleVisualParams,
} from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';

const KM_PER_LATITUDE_DEGREE = 111.32;

type PolygonOffsetsKm = ReadonlyArray<readonly [number, number]>;

// Outline of the sample boundary polygon as [east, north] offsets in km from the sample center.
const SAMPLE_BOUNDARY_POLYGON_OFFSETS_KM: PolygonOffsetsKm = [
  [9, 0.9],
  [8, -0.6],
  [5.9, -3.8],
  [2.3, -4.4],
  [-0.3, -7.5],
  [-4.7, -5.6],
  [-4.9, -2.1],
  [-6.5, -2.9],
  [-8, -0.6],
  [-6.6, 1],
  [-5.2, 3.5],
  [-5.1, 6.7],
  [1.2, 5.9],
  [1.6, 4.4],
  [3.2, 5],
  [5.6, 4.8],
];

// Outline of the sample drive time polygon, same format as the boundary one.
const SAMPLE_DRIVE_TIME_POLYGON_OFFSETS_KM: PolygonOffsetsKm = [
  [5, -0.6],
  [5.1, -1.1],
  [4.7, -1.6],
  [4.6, -2.2],
  [4.6, -2.9],
  [5, -4.1],
  [4.6, -4.6],
  [3.5, -4.4],
  [3, -4.8],
  [2.4, -4.9],
  [1.9, -5.2],
  [1.3, -5.4],
  [0.7, -6.1],
  [-0.1, -7.3],
  [-0.8, -7.1],
  [-1.4, -6.3],
  [-1.7, -5],
  [-2.5, -5.1],
  [-2.8, -4.5],
  [-3.3, -4.2],
  [-3.9, -3.9],
  [-4.7, -3.7],
  [-4.5, -2.8],
  [-4.3, -2.1],
  [-4.6, -1.6],
  [-5.1, -1.1],
  [-5.3, -0.5],
  [-3.7, 0],
  [-4.5, 0.6],
  [-3.5, 0.7],
  [-4.2, 1.4],
  [-4, 2.1],
  [-3.3, 2],
  [-3.8, 3.1],
  [-3.7, 3.5],
  [-2.8, 3.5],
  [-3.1, 4.8],
  [-2.8, 5.4],
  [-2, 5.5],
  [-1.5, 6.1],
  [-0.7, 6.1],
  [0, 6.5],
  [0.8, 7.5],
  [1.5, 6.2],
  [1.8, 5.5],
  [2.5, 5.4],
  [3.2, 5.2],
  [3.6, 4.5],
  [3.2, 3.2],
  [3.7, 3],
  [4.8, 3.1],
  [4.9, 2.4],
  [5, 1.9],
  [6, 1.5],
  [6.4, 0.8],
  [5.7, -0.1],
];

const SAMPLE_DRIVE_TIME_MINUTES = 15;

export const isReportTypePolygon = (type: ExportReportType) => (
  type === ExportReportType.Polygon || type === ExportReportType.DriveTimePolygon
);

type SampleReportData = {
  center: LatLng;
  heading: string;
  semiHeading: string;
  polygonName: string;
};

export const getReportSampleData = (country: AgsReportCountry): SampleReportData => {
  switch (country) {
    case 'can':
      // Montréal
      return {
        heading: 'Parc La Fontaine',
        semiHeading: 'Montréal, QC',
        polygonName: 'Parc La Fontaine',
        center: {
          lat: 45.5279,
          lng: -73.5704,
        },
      };
    case 'mex':
      // Mérida
      return {
        heading: 'Plaza Grande',
        semiHeading: 'Mérida, Yuc.',
        polygonName: 'Plaza Grande',
        center: {
          lat: 20.9672,
          lng: -89.6238,
        },
      };
    case 'usa':
    default:
      // Portland
      return {
        heading: 'Forest Park',
        semiHeading: 'Portland, OR',
        polygonName: 'Forest Park',
        center: {
          lat: 45.5734,
          lng: -122.7686,
        },
      };
  }
};

export const createReportSampleRadiusParams = (sampleData: SampleReportData): ExportReportRadiusParams => ({
  type: ExportReportType.Radius,
  data: {
    center: sampleData.center,
    radius: 5,
    title: sampleData.heading,
    semiHeading: sampleData.semiHeading,
    unit: UnitSystem.imperial,
  },
});

const createReportSamplePolygonPaths = (center: LatLng, offsetsKm: PolygonOffsetsKm): MultiPolygon => {
  const kmPerLongitudeDegree = KM_PER_LATITUDE_DEGREE * Math.cos(center.lat * Math.PI / 180);

  return [{
    path: offsetsKm.map(([eastKm, northKm]) => ({
      lat: center.lat + northKm / KM_PER_LATITUDE_DEGREE,
      lng: center.lng + eastKm / kmPerLongitudeDegree,
    })),
    holes: [],
  }];
};

const createReportSampleVisualParams = (type: ExportReportType, sampleData: SampleReportData): ExportReportSampleVisualParams => {
  switch (type) {
    case ExportReportType.Location:
      return {
        type: ExportReportType.Location,
        data: {
          center: sampleData.center,
          summary: {
            heading: sampleData.heading,
            semiHeading: sampleData.semiHeading,
          },
        },
      };

    case ExportReportType.Polygon:
      return {
        type: ExportReportType.Polygon,
        data: {
          heading: sampleData.polygonName,
          paths: createReportSamplePolygonPaths(sampleData.center, SAMPLE_BOUNDARY_POLYGON_OFFSETS_KM),
        },
      };

    case ExportReportType.DriveTimePolygon:
      return {
        type: ExportReportType.DriveTimePolygon,
        data: {
          center: sampleData.center,
          minutes: SAMPLE_DRIVE_TIME_MINUTES,
          paths: createReportSamplePolygonPaths(sampleData.center, SAMPLE_DRIVE_TIME_POLYGON_OFFSETS_KM),
        },
      };

    case ExportReportType.Radius:
    default:
      return createReportSampleRadiusParams(sampleData);
  }
};

export const createReportSampleParams = (type: ExportReportType, country: AgsReportCountry): ExportReportSampleParams => {
  const sampleData = getReportSampleData(country);

  return {
    center: sampleData.center,
    visualParams: createReportSampleVisualParams(type, sampleData),
  };
};
